import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import Lottie from "lottie-react";
import searchAnimation from "../../Assets/Loader/search.json";
import { FiMail, FiShield } from "react-icons/fi";
import DriftWall from "../../Components/SignIn/DriftWall";
import { APPRoutes } from "../../Utils/Route";
import { forgotPassword } from "../../Services/AuthenticationService";
import { toast } from "react-toastify";

// Product screenshots that drift behind the card. Loaded in bulk from
// the pre-compressed Driftwall_images folder so the background stays lightweight.
const picsContext = require.context("../../Assets/Images/Driftwall_images", false, /\.jpe?g$/);
const DRIFT_WALL_ITEMS = picsContext.keys().map((key) => ({ image: picsContext(key) }));

const ForgetPasswordPage = () => {
  const [submitState, setSubmitState] = useState("idle"); // idle | loading | success | error

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      username: "",
    },
    validationSchema: Yup.object({
      username: Yup.string()
        .trim()
        .required("Email is required")
        .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, "Enter a valid email address"),
    }),
    onSubmit: async (values) => {
      setSubmitState("loading");
      try {
        const response = await forgotPassword(values.username);
        if (response?.is_success) {
          setSubmitState("success");
          toast.success(response?.message || "Password reset link sent successfully.");
        } else if (response?.is_success === false && response?.status_code === 500) {
          setSubmitState("error");
          toast.error("Internal Server Error. Please Contact Support Team.");
        } else {
          setSubmitState("error");
          toast.error(response?.message || "Failed to send password reset link. Please try again.");
        }
      } catch (error) {
        setSubmitState("error");
        toast.error(error?.message || "An unexpected error occurred. Please try again later.");
        console.error("Forgot password error:", error);
      }
    },
  });

  const getFieldError = (fieldName) =>
    formik.touched[fieldName] && formik.errors[fieldName] ? formik.errors[fieldName] : "";

  const renderButtonContent = () => {
    if (submitState === "loading") {
      return (
        <Lottie
          animationData={searchAnimation}
          loop={true}
          style={{ width: "3.5rem", height: "3.5rem" }}
        />
      );
    }
    return "Send Reset Link";
  };

  return (
    <div data-bs-theme="light" className="hc-auth-shell position-relative min-vh-100 w-100 overflow-hidden">
      <div className="hc-auth-driftwall-wrap position-absolute">
        <DriftWall items={DRIFT_WALL_ITEMS} fade={0.05} />
      </div>

      <div className="hc-auth-center position-relative min-vh-100 w-100 d-flex align-items-center justify-content-center pe-none">
        <div className="hc-auth-panel position-relative w-100 d-flex flex-column justify-content-center pe-auto overflow-hidden">
          <div className="hc-auth-header d-flex flex-column align-items-start">
            <div className="hc-auth-hero-pill align-self-start d-inline-flex align-items-center py-1 px-3 rounded-pill text-nowrap pe-none">
              <FiShield size={14} className="flex-shrink-0" />
              <span>Secure connection</span>
            </div>

            <div className="hc-auth-header-row d-flex align-items-center w-100">
              <img
                src="https://www.aegishs.us/aegis-img/badge_logo_ring.png"
                alt="Aegis 360"
                className="hc-auth-logo object-fit-contain"
              />
              <div className="hc-auth-header-text flex-fill">
                <h2 className="hc-auth-title text-start m-0">
                  Forgot <span className="hc-auth-title-accent">Password</span>
                </h2>
                <p className="hc-auth-subtitle text-start mt-1 mx-0 mb-0">
                  Enter your email to receive a reset link
                </p>
              </div>
            </div>
          </div>

          <div className="hc-auth-panel-inner">
            <form onSubmit={formik.handleSubmit} noValidate>
              <div className="hc-auth-field">
                <label htmlFor="username" className="hc-auth-field-label d-block fs-6">
                  Email
                </label>
                <div className={`hc-auth-underline d-flex align-items-center gap-2${getFieldError("username") ? " is-invalid" : ""}`}>
                  <input
                    name="username"
                    id="username"
                    type="text"
                    className="hc-auth-input border-0 rounded-0 shadow-none fw-bold p-0"
                    placeholder="you@example.com"
                    autoComplete="username"
                    value={formik.values.username}
                    onChange={(event) => {
                      formik.handleChange(event);
                      if (submitState !== "idle") setSubmitState("idle");
                    }}
                    onBlur={formik.handleBlur}
                    aria-invalid={Boolean(getFieldError("username"))}
                  />
                  <span className="hc-auth-field-icon d-flex align-items-center border-0 p-0">
                    <FiMail size={20} />
                  </span>
                </div>
                <div className="hc-auth-field-error">{getFieldError("username")}</div>
              </div>

              <button
                type="submit"
                className={`btn hc-auth-submit-btn rounded-pill mt-2 w-100 fw-semibold border-0 fs-5 d-flex align-items-center justify-content-center ${submitState === "loading" ? "p-0" : "py-2"}`}
                disabled={submitState === "loading" || submitState === "success"}
                style={{
                  color: "var(--hc-white)",
                  boxShadow: "0 14px 32px rgba(var(--hc-black-rgb), 0.25)",
                  opacity: submitState === "loading" ? 0.7 : 1,
                  minHeight: "3rem",
                }}
              >
                {renderButtonContent()}
              </button>
            </form>

            <p className="mt-3 mb-0 text-center fs-6 hc-auth-footer-text">
              Remember your password?
              <Link to={APPRoutes.LOGIN} className="ms-1 text-decoration-none text-nowrap hc-auth-footer-link fw-bold">
                Sign In
              </Link>
            </p>

            <p className="hc-auth-trust-line d-flex align-items-center justify-content-center text-nowrap">
              <FiShield size={13} />
              Secured with encryption &middot; HIPAA compliant
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgetPasswordPage;
